/**
 * Notification and Reminder Service
 * Handles task status, deadline tracking, and notification generation
 */

import { Task, Epic, Project, Sprint, UpdateStatus, Notification, Reminder, User } from '../types';
import { generateId } from '../utils/idGenerator';
import { parseDateLike } from '../utils/dateUtils';

export type TaskHealthStatus = 'healthy' | 'needs-update' | 'at-risk' | 'overdue' | 'blocked';

export interface TaskHealth {
  status: TaskHealthStatus;
  color: string;
  bgColor: string;
  borderColor: string;
  message: string;
  daysUntilDeadline?: number;
  daysSinceUpdate?: number;
  requiresAction: boolean;
}

/**
 * Calculate the health status of a task based on deadlines and updates
 */
export function getTaskHealth(task: Task): TaskHealth {
  const now = new Date();
  const updatedAt = parseDateLike(task.updatedAt) || now;
  const daysSinceUpdate = Math.floor((now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24));

  // Check if blocked
  if (task.status === 'Blocked') {
    return {
      status: 'blocked',
      color: 'text-chart-4',
      bgColor: 'bg-chart-4/10',
      borderColor: 'border-chart-4/40',
      message: 'Task is blocked',
      daysSinceUpdate,
      requiresAction: true,
    };
  }

  // Check if completed
  if (task.status === 'Completed') {
    return {
      status: 'healthy',
      color: 'text-chart-2',
      bgColor: 'bg-chart-2/10',
      borderColor: 'border-chart-2/40',
      message: 'Task completed',
      daysSinceUpdate,
      requiresAction: false,
    };
  }

  // Check deadline
  if (task.targetEndDate) {
    const deadline = parseDateLike(task.targetEndDate);
    if (!deadline) {
      return {
        status: 'needs-update',
        color: 'text-chart-5',
        bgColor: 'bg-chart-5/10',
        borderColor: 'border-chart-5/40',
        message: `No update in ${daysSinceUpdate} days`,
        daysSinceUpdate,
        requiresAction: true,
      };
    }

    const daysUntilDeadline = Math.floor((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    // Overdue
    if (daysUntilDeadline < 0) {
      return {
        status: 'overdue',
        color: 'text-destructive',
        bgColor: 'bg-destructive/10',
        borderColor: 'border-destructive/40',
        message: `Overdue by ${Math.abs(daysUntilDeadline)} day${Math.abs(daysUntilDeadline) !== 1 ? 's' : ''}`,
        daysUntilDeadline,
        daysSinceUpdate,
        requiresAction: true,
      };
    }

    // At risk (deadline within 3 days)
    if (daysUntilDeadline <= 3) {
      return {
        status: 'at-risk',
        color: 'text-chart-5',
        bgColor: 'bg-chart-5/10',
        borderColor: 'border-chart-5/40',
        message: `Due in ${daysUntilDeadline} day${daysUntilDeadline !== 1 ? 's' : ''}`,
        daysUntilDeadline,
        daysSinceUpdate,
        requiresAction: true,
      };
    }

    // Needs update (no update in 7+ days and not near deadline)
    if (daysSinceUpdate >= 7) {
      return {
        status: 'needs-update',
        color: 'text-chart-5',
        bgColor: 'bg-chart-5/10',
        borderColor: 'border-chart-5/40',
        message: `No update in ${daysSinceUpdate} days`,
        daysUntilDeadline,
        daysSinceUpdate,
        requiresAction: true,
      };
    }

    // Healthy
    return {
      status: 'healthy',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/40',
      message: `On track (due in ${daysUntilDeadline} days)`,
      daysUntilDeadline,
      daysSinceUpdate,
      requiresAction: false,
    };
  }

  // No deadline set - check update frequency
  if (daysSinceUpdate >= 7) {
    return {
      status: 'needs-update',
      color: 'text-chart-5',
      bgColor: 'bg-chart-5/10',
      borderColor: 'border-chart-5/40',
      message: `No update in ${daysSinceUpdate} days`,
      daysSinceUpdate,
      requiresAction: true,
    };
  }

  return {
    status: 'healthy',
    color: 'text-muted-foreground',
    bgColor: 'bg-muted/40',
    borderColor: 'border-border',
    message: 'No deadline set',
    daysSinceUpdate,
    requiresAction: false,
  };
}

/**
 * Get all at-risk tasks for a project
 */
export function getAtRiskTasks(tasks: Task[]): Task[] {
  return tasks.filter(task => {
    const health = getTaskHealth(task);
    return health.status === 'at-risk' || health.status === 'overdue' || health.status === 'blocked';
  });
}

/**
 * Get tasks that need updates
 */
export function getTasksNeedingUpdate(tasks: Task[]): Task[] {
  return tasks.filter(task => {
    const health = getTaskHealth(task);
    return health.requiresAction;
  });
}

/**
 * Calculate update status for a task
 */
export function getUpdateStatus(task: Task): UpdateStatus {
  const now = new Date();
  const lastUpdateDate = task.updatedAt;
  const parsedLastUpdate = parseDateLike(lastUpdateDate) || now;
  const daysSinceUpdate = Math.floor((now.getTime() - parsedLastUpdate.getTime()) / (1000 * 60 * 60 * 24));
  
  // Determine if update is required based on task status and time
  const requiresUpdate = daysSinceUpdate >= 7 && task.status !== 'Completed';

  return {
    lastUpdateDate,
    daysOverdue: daysSinceUpdate >= 7 ? daysSinceUpdate - 7 : undefined,
    requiresUpdate,
  };
}

/**
 * Generate notifications for overdue updates
 */
export function generateUpdateNotifications(
  tasks: Task[],
  users: User[],
  organizationId: string
): Notification[] {
  const notifications: Notification[] = [];
  const now = new Date().toISOString();

  tasks.forEach(task => {
    const health = getTaskHealth(task);
    
    if (health.requiresAction && (task.assigneeIds || []).length > 0) {
      (task.assigneeIds || []).forEach(userId => {
        const user = users.find(u => u.id === userId);
        if (!user) return;

        let type: Notification['type'] = 'reminder';
        let priority: Notification['priority'] = 'medium';
        let title = '';
        let message = '';

        if (health.status === 'overdue') {
          type = 'deadline';
          priority = 'urgent';
          title = `Task Overdue: ${task.title}`;
          message = health.message;
        } else if (health.status === 'at-risk') {
          type = 'deadline';
          priority = 'high';
          title = `Task Due Soon: ${task.title}`;
          message = health.message;
        } else if (health.status === 'needs-update') {
          type = 'update_overdue';
          priority = 'medium';
          title = `Update Required: ${task.title}`;
          message = `This task hasn't been updated in ${health.daysSinceUpdate} days. Please provide a status update.`;
        } else if (health.status === 'blocked') {
          type = 'status_change';
          priority = 'high';
          title = `Task Blocked: ${task.title}`;
          message = 'This task is marked as blocked. Please resolve blockers or update status.';
        }

        notifications.push({
          id: generateId('notif', task.id, userId),
          organizationId,
          userId,
          type,
          priority,
          title,
          message,
          entityType: 'task',
          entityId: task.id,
          actionUrl: `/app/projects/${task.projectId}/tasks?taskId=${task.id}`,
          read: false,
          createdAt: now,
        });
      });
    }
  });

  return notifications;
}

/**
 * Check if task needs next update date prompt
 */
export function shouldPromptForUpdateDate(task: Task): boolean {
  const health = getTaskHealth(task);
  return health.requiresAction && task.status !== 'Completed';
}

/**
 * Generate reminder for task deadline
 */
export function createDeadlineReminder(
  task: Task,
  recipientIds: string[],
  createdBy: string,
  daysBeforeDeadline: number = 3
): Reminder | null {
  if (!task.targetEndDate) return null;

  const deadline = parseDateLike(task.targetEndDate);
  if (!deadline) return null;

  const reminderDate = new Date(deadline);
  reminderDate.setDate(reminderDate.getDate() - daysBeforeDeadline);

  return {
    id: generateId('reminder', task.id),
    entityType: 'task',
    entityId: task.id,
    entityTitle: task.title,
    reminderType: 'deadline',
    reminderDate: reminderDate.toISOString(),
    message: `Task "${task.title}" is due in ${daysBeforeDeadline} days`,
    recipientIds,
    notificationChannels: ['app', 'email'],
    status: 'pending',
    createdBy,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Create update reminder for task
 */
export function createUpdateReminder(
  task: Task,
  nextExpectedUpdateDate: string,
  recipientIds: string[],
  createdBy: string,
  recurring?: { frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' }
): Reminder {
  return {
    id: generateId('reminder-update', task.id),
    entityType: 'task',
    entityId: task.id,
    entityTitle: task.title,
    reminderType: 'update',
    reminderDate: nextExpectedUpdateDate,
    nextExpectedUpdateDate,
    message: `Please provide an update for task "${task.title}"`,
    recipientIds,
    notificationChannels: ['app'],
    status: 'pending',
    createdBy,
    createdAt: new Date().toISOString(),
    recurring,
  };
}

/**
 * Get project health summary
 */
export function getProjectHealth(tasks: Task[]): {
  healthy: number;
  needsUpdate: number;
  atRisk: number;
  overdue: number;
  blocked: number;
  total: number;
} {
  const summary = {
    healthy: 0,
    needsUpdate: 0,
    atRisk: 0,
    overdue: 0,
    blocked: 0,
    total: tasks.length,
  };

  tasks.forEach(task => {
    const health = getTaskHealth(task);
    switch (health.status) {
      case 'healthy':
        summary.healthy++;
        break;
      case 'needs-update':
        summary.needsUpdate++;
        break;
      case 'at-risk':
        summary.atRisk++;
        break;
      case 'overdue':
        summary.overdue++;
        break;
      case 'blocked':
        summary.blocked++;
        break;
    }
  });

  return summary;
}

/**
 * Sort tasks by urgency (most urgent first)
 */
export function sortTasksByUrgency(tasks: Task[]): Task[] {
  const urgencyOrder: Record<TaskHealthStatus, number> = {
    'overdue': 0,
    'blocked': 1,
    'at-risk': 2,
    'needs-update': 3,
    'healthy': 4,
  };

  return [...tasks].sort((a, b) => {
    const healthA = getTaskHealth(a);
    const healthB = getTaskHealth(b);
    return urgencyOrder[healthA.status] - urgencyOrder[healthB.status];
  });
}
